import { Panel, PageHeader } from "@/components/admin-shell";
import { apiRequest } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  ShoppingBag,
  Package,
  AlertTriangle,
  ArrowUpRight,
  Sparkles,
  Users,
} from "lucide-react";

interface IProduct {
  _id: string;
  price: number;
  sizes: { ml: number; price: number; stock: number }[];
}

interface IOrder {
  _id: string;
  orderNumber: string;
  shipping: {
    name: string;
    district: string;
  };
  paymentMethod: string;
  total: number;
  status: string;
  createdAt: string;
}

export default function Overview() {
  const fmt = (n: number) => `৳${n.toLocaleString("en-BD")}`;

  // Fetch Products
  const { data: products = [] } = useQuery<IProduct[]>({
    queryKey: ["products"],
    queryFn: () => apiRequest<IProduct[]>("/products?isAdmin=true"),
  });

  // Fetch Orders
  const { data: orders = [] } = useQuery<IOrder[]>({
    queryKey: ["orders"],
    queryFn: () => apiRequest<IOrder[]>("/orders"),
  });

  // Calculations
  const revenue = orders.reduce((sum, o) => sum + (o.status !== "cancelled" ? o.total : 0), 0);
  const totalOrders = orders.length;

  const lowStock = products.filter((p) => {
    const totalStock = p.sizes.reduce((sum, s) => sum + s.stock, 0);
    return totalStock > 0 && totalStock < 5;
  }).length;

  const stats = [
    { label: "Total Revenue", value: fmt(revenue), delta: "Live Data", icon: TrendingUp },
    { label: "Total Orders", value: totalOrders, delta: "Live Data", icon: ShoppingBag },
    { label: "Low Stock Items", value: lowStock, delta: "Needs Refill", icon: AlertTriangle },
    { label: "Active Products", value: products.length, delta: "In Catalog", icon: Package },
  ];

  return (
    <>
      <PageHeader
        title="Dashboard Overview"
        subtitle="A real-time look at how the house of fragrance is performing."
      />

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Panel key={s.label} className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-soft">
                  {s.label}
                </div>
                <div className="mt-2 font-display text-3xl text-foreground">{s.value}</div>
              </div>
              <div className="rounded-md border border-gold/30 p-2 text-gold">
                <s.icon className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 text-xs text-gold">
              <ArrowUpRight className="h-3 w-3" /> {s.delta}
            </div>
          </Panel>
        ))}
      </div>

      {/* Sales chart + alerts */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          title="Order Volume by Status"
          description="Distribution of orders currently in the system"
        >
          <div className="flex h-64 items-stretch gap-3 justify-around pt-6">
            {["pending", "confirmed", "shipped", "delivered", "cancelled"].map((status) => {
              const count = orders.filter((o) => o.status === status).length;
              const maxCount = Math.max(
                ...["pending", "confirmed", "shipped", "delivered", "cancelled"].map(
                  (st) => orders.filter((o) => o.status === st).length,
                ),
                1,
              );
              const pct = (count / maxCount) * 100;
              return (
                <div key={status} className="group flex flex-col items-center gap-2 w-12">
                  <div className="relative w-full flex-1 flex items-end justify-center">
                    <div
                      className="w-8 rounded-t-md bg-overview-bar-gradient border-t border-gold/40 transition-all group-hover:opacity-80"
                      style={{ height: `${Math.max(pct, 5)}%` }}
                    />
                    <div className="absolute -top-7 rounded bg-card px-2 py-0.5 text-[10px] text-gold opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                      {count} order{count !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="text-[11px] capitalize tracking-widest text-muted-foreground truncate w-full text-center ">
                    {status}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="Live alerts" description="Things that need your attention">
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3 rounded-md border border-gold/15 bg-accent/30 p-3">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-gold" />
              <div>
                <div className="text-foreground">{lowStock} products low on stock</div>
                <div className="text-xs text-muted-foreground">
                  Keep catalogue items well-stocked.
                </div>
              </div>
            </li>
            <li className="flex items-start gap-3 rounded-md border border-gold/15 bg-accent/30 p-3">
              <Sparkles className="h-4 w-4 mt-0.5 text-gold" />
              <div>
                <div className="text-foreground">System Connected</div>
                <div className="text-xs text-muted-foreground">
                  Express API connected to MongoDB cluster.
                </div>
              </div>
            </li>
            <li className="flex items-start gap-3 rounded-md border border-gold/15 bg-accent/30 p-3">
              <Users className="h-4 w-4 mt-0.5 text-gold" />
              <div>
                <div className="text-foreground">Fulfillment Active</div>
                <div className="text-xs text-muted-foreground">
                  Real-time status updates synced.
                </div>
              </div>
            </li>
          </ul>
        </Panel>
      </div>

      {/* Recent orders */}
      <div className="mt-6">
        <Panel title="Recent orders" description="Latest activity from across Bangladesh">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-gold-soft">
                  <th className="px-4 py-3 first:pl-5 last:pr-5">Order</th>
                  <th className="px-4 py-3 first:pl-5 last:pr-5">Customer</th>
                  <th className="px-4 py-3 first:pl-5 last:pr-5">District</th>
                  <th className="px-4 py-3 first:pl-5 last:pr-5">Payment</th>
                  <th className="px-4 py-3 first:pl-5 last:pr-5 text-right">Total</th>
                  <th className="px-4 py-3 first:pl-5 last:pr-5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold/10">
                {orders.slice(0, 6).map((o) => (
                  <tr key={o._id} className="hover:bg-accent/40">
                    <td className="px-4 py-3.5 first:pl-5 last:pr-5 font-mono text-gold">
                      {o.orderNumber}
                    </td>
                    <td className="px-4 py-3.5 first:pl-5 last:pr-5">{o.shipping.name}</td>
                    <td className="px-4 py-3.5 first:pl-5 last:pr-5 text-muted-foreground">
                      {o.shipping.district}
                    </td>
                    <td className="px-4 py-3.5 first:pl-5 last:pr-5 text-muted-foreground uppercase">
                      {o.paymentMethod}
                    </td>
                    <td className="px-4 py-3.5 first:pl-5 last:pr-5 text-right font-medium">
                      {fmt(o.total)}
                    </td>
                    <td className="px-4 py-3.5 first:pl-5 last:pr-5">
                      <StatusPill status={o.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    delivered: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    shipped: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    processing: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    confirmed: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    pending: "bg-gold/10 text-gold border-gold/30",
    cancelled: "bg-red-500/10 text-red-300 border-red-500/30",
  };
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] capitalize ${map[status] ?? ""}`}
    >
      {status}
    </span>
  );
}
